/**
 * Safe ResizeObserver utilities to prevent infinite loops
 */

/**
 * Creates a debounced function that delays execution until after wait milliseconds
 * @param {Function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds
 * @param {Object} options - Options for debouncing
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait, options = {}) => {
  let timeout;
  let maxWait;
  let maxWaitTimeout;
  let lastArgs;
  let lastThis;
  let lastCallTime;
  let lastInvokeTime = 0;
  let result;
  let leading = false;
  let trailing = true;

  if (typeof func !== 'function') {
    throw new TypeError('Expected a function');
  }

  wait = +wait || 0;
  if (typeof options === 'object') {
    leading = !!options.leading;
    maxWait = 'maxWait' in options;
    maxWait = maxWait ? (+options.maxWait || 0) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    const args = lastArgs;
    const thisArg = lastThis;
    const result = func.apply(thisArg, args);
    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    return result;
  }

  function leadingEdge(time) {
    lastInvokeTime = time;
    timeout = setTimeout(timerExpired, wait);
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;
    return maxWait ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxWait && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timeout = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timeout = undefined;
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timeout = undefined;
  }

  function flush() {
    return timeout === undefined ? result : trailingEdge(Date.now());
  }

  function pending() {
    return timeout !== undefined;
  }

  function debounced(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timeout === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxWait) {
        timeout = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timeout === undefined) {
      timeout = setTimeout(timerExpired, wait);
    }
    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;
  debounced.pending = pending;
  return debounced;
};

/**
 * Creates a throttled function that only executes at most once per wait period
 * @param {Function} func - Function to throttle
 * @param {number} wait - Delay in milliseconds
 * @param {Object} options - Options for throttling
 * @returns {Function} Throttled function
 */
export const throttle = (func, wait, options = {}) => {
  let leading = true;
  let trailing = true;

  if (typeof func !== 'function') {
    throw new TypeError('Expected a function');
  }
  if (typeof options === 'object') {
    leading = 'leading' in options ? !!options.leading : leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }
  return debounce(func, wait, {
    leading,
    trailing,
    'maxWait': wait
  });
};

/**
 * Safe ResizeObserver wrapper that prevents infinite loops
 */
export class SafeResizeObserver {
  constructor(callback, options = {}) {
    this.callback = callback;
    this.options = {
      debounceMs: 16, // ~60fps
      maxWaitMs: 100,
      ...options
    };
    
    // Debounce the callback to prevent rapid-fire calls
    this.debouncedCallback = debounce(this.handleResize.bind(this), this.options.debounceMs, {
      leading: true,
      trailing: true,
      maxWait: this.options.maxWaitMs
    });

    // Track observed elements to prevent duplicate observations
    this.observedElements = new WeakSet();
    
    // Create the actual ResizeObserver
    this.observer = new ResizeObserver(this.debouncedCallback);
  }

  handleResize(entries, observer) {
    try {
      // Filter out entries that might cause loops
      const safeEntries = entries.filter(entry => {
        const element = entry.target;
        
        // Skip if element is not in DOM
        if (!document.contains(element)) {
          return false;
        }
        
        // Skip if element has no dimensions
        if (entry.contentRect.width === 0 && entry.contentRect.height === 0) {
          return false;
        }
        
        return true;
      });

      if (safeEntries.length > 0) {
        this.callback(safeEntries, observer);
      }
    } catch (error) {
      console.warn('SafeResizeObserver: Error in resize callback:', error);
    }
  }

  observe(element) {
    if (!this.observedElements.has(element)) {
      this.observedElements.add(element);
      this.observer.observe(element);
    }
  }

  unobserve(element) {
    if (this.observedElements.has(element)) {
      this.observedElements.delete(element);
      this.observer.unobserve(element);
    }
  }

  disconnect() {
    this.observedElements = new WeakSet();
    this.observer.disconnect();
  }
}

/**
 * Creates a safe resize event handler that prevents infinite loops
 * @param {Function} callback - Function to call on resize
 * @param {Object} options - Options for the handler
 * @returns {Function} Cleanup function
 */
export const createSafeResizeHandler = (callback, options = {}) => {
  const {
    debounceMs = 16,
    maxWaitMs = 100,
    element = window
  } = options;

  const debouncedCallback = debounce(callback, debounceMs, {
    leading: true,
    trailing: true,
    maxWait: maxWaitMs
  });

  element.addEventListener('resize', debouncedCallback);

  return () => {
    element.removeEventListener('resize', debouncedCallback);
  };
};

/**
 * Creates a safe ResizeObserver for a specific element
 * @param {Element} element - Element to observe
 * @param {Function} callback - Callback function
 * @param {Object} options - Options for the observer
 * @returns {SafeResizeObserver} Safe ResizeObserver instance
 */
export const createSafeResizeObserver = (element, callback, options = {}) => {
  const observer = new SafeResizeObserver(callback, options);
  observer.observe(element);
  return observer;
};

/**
 * Prevents ResizeObserver loops by using requestAnimationFrame
 * @param {Function} callback - Function to execute safely
 * @returns {Function} Wrapped function
 */
export const withRAF = (callback) => {
  let rafId = null;
  
  return (...args) => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    
    rafId = requestAnimationFrame(() => {
      try {
        callback(...args);
      } catch (error) {
        console.warn('withRAF: Error in callback:', error);
      } finally {
        rafId = null;
      }
    });
  };
};

/**
 * Detects if ResizeObserver is causing a loop
 * @param {Element} element - Element being observed
 * @param {number} threshold - Number of rapid changes to consider a loop
 * @returns {boolean} True if loop detected
 */
export const detectResizeLoop = (() => {
  const changeCounts = new WeakMap();
  const lastChangeTime = new WeakMap();
  
  return (element, threshold = 10) => {
    const now = Date.now();
    const lastTime = lastChangeTime.get(element) || 0;
    const count = changeCounts.get(element) || 0;
    
    if (now - lastTime < 100) { // Within 100ms
      const newCount = count + 1;
      changeCounts.set(element, newCount);
      lastChangeTime.set(element, now);
      
      if (newCount > threshold) {
        console.warn('ResizeObserver loop detected for element:', element);
        return true;
      }
    } else {
      // Reset counter if enough time has passed
      changeCounts.set(element, 1);
      lastChangeTime.set(element, now);
    }
    
    return false;
  };
})();

/**
 * Sets up global ResizeObserver error handling
 */
export const setupResizeObserverErrorHandler = () => {
  if (typeof window === 'undefined') return;

  // Override the global error handler to catch ResizeObserver errors
  const originalErrorHandler = window.onerror;
  
  window.onerror = (message, source, lineno, colno, error) => {
    // Check if it's a ResizeObserver error
    if (message && message.includes('ResizeObserver loop completed with undelivered notifications')) {
      console.warn('ResizeObserver loop error caught and suppressed:', message);
      
      // Log to Sentry if available
      if (window.Sentry) {
        window.Sentry.captureException(new Error(message), {
          tags: {
            errorType: 'RESIZE_OBSERVER_LOOP',
            source: source,
            line: lineno,
            column: colno
          },
          level: 'warning'
        });
      }
      
      // Suppress the error to prevent it from showing in console
      return true;
    }
    
    // Call original error handler for other errors
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }
    
    return false;
  };

  // Also handle unhandled promise rejections that might contain ResizeObserver errors
  const originalRejectionHandler = window.onunhandledrejection;
  
  window.onunhandledrejection = (event) => {
    if (event.reason && 
        typeof event.reason === 'string' && 
        event.reason.includes('ResizeObserver loop completed with undelivered notifications')) {
      console.warn('ResizeObserver loop error in promise rejection caught and suppressed:', event.reason);
      
      // Log to Sentry if available
      if (window.Sentry) {
        window.Sentry.captureException(new Error(event.reason), {
          tags: {
            errorType: 'RESIZE_OBSERVER_LOOP_PROMISE',
            rejectionValue: event.reason
          },
          level: 'warning'
        });
      }
      
      // Prevent the default behavior
      event.preventDefault();
      return;
    }
    
    // Call original rejection handler for other rejections
    if (originalRejectionHandler) {
      return originalRejectionHandler(event);
    }
  };

  console.log('ResizeObserver error handler initialized');
};
